import { ErrorHandler, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

@Injectable()
export class CygovErrorHandler implements ErrorHandler {
  constructor(private logger: NGXLogger) {}

  handleError(error: any): void {
    const normalizedError = this.normalizeError(error);
    const context = this.getRuntimeContext();
    const fingerprint = this.buildFingerprint(normalizedError, context);

    // Structured payload to simplify monitoring/searching in external log pipelines.
    const payload = {
      level: 'error',
      source: 'frontend-global-error-handler',
      fingerprint,
      ...normalizedError,
      context,
    };

    this.logger.error('CygovErrorHandler captured unhandled error', payload);
  }

  private normalizeError(error: any): {
    errorType: string;
    message: string;
    stack?: string;
    sourceFile?: string;
    sourceLine?: number;
    sourceColumn?: number;
    rejectionReason?: string;
    raw?: string;
  } {
    const fallbackMessage = 'Unhandled error with empty payload';

    if (!error) {
      return {
        errorType: 'UnknownError',
        message: fallbackMessage,
      };
    }

    // Handles ErrorEvent shape from browser/global errors.
    if (error?.filename || error?.lineno || error?.colno) {
      return {
        errorType: error?.name || 'ErrorEvent',
        message: error?.message || fallbackMessage,
        stack: error?.error?.stack,
        sourceFile: error?.filename,
        sourceLine: error?.lineno,
        sourceColumn: error?.colno,
        raw: this.limit(this.safeStringify(error), 4000),
      };
    }

    // Handles Promise rejection style payloads.
    if (error?.rejection) {
      return {
        errorType: error?.name || 'UnhandledPromiseRejection',
        message: error?.message || fallbackMessage,
        stack: error?.stack || error?.rejection?.stack,
        rejectionReason: this.limit(this.safeStringify(error?.rejection), 2000),
        raw: this.limit(this.safeStringify(error), 4000),
      };
    }

    return {
      errorType: error?.name || typeof error,
      message: error?.message || error?.toString?.() || fallbackMessage,
      stack: error?.stack,
      raw: this.limit(this.safeStringify(error), 4000),
    };
  }

  private getRuntimeContext(): {
    timestamp: string;
    url: string;
    path: string;
    referrer?: string;
    userAgent?: string;
    language?: string;
    platform?: string;
    timezone?: string;
    screenResolution?: string;
    viewport?: string;
  } {
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';

    return {
      timestamp: new Date().toISOString(),
      url: hasWindow ? window.location.href : '',
      path: hasWindow ? `${window.location.pathname}${window.location.search}` : '',
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      userAgent: hasNavigator ? navigator.userAgent : undefined,
      language: hasNavigator ? navigator.language : undefined,
      platform: hasNavigator ? navigator.platform : undefined,
      timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone,
      screenResolution: hasWindow && window.screen ? `${window.screen.width}x${window.screen.height}` : undefined,
      viewport: hasWindow ? `${window.innerWidth}x${window.innerHeight}` : undefined,
    };
  }

  private buildFingerprint(
    error: { message: string; stack?: string; sourceFile?: string },
    context: { path: string }
  ): string {
    const signature = `${error.message}|${error.stack || ''}|${error.sourceFile || ''}|${context.path || ''}`;
    let hash = 0;
    const mod = 2147483647;

    for (let i = 0; i < signature.length; i++) {
      hash = (hash * 31 + signature.charCodeAt(i)) % mod;
    }

    return `ce-${hash}`;
  }

  private safeStringify(value: any): string {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Unserializable value]';
    }
  }

  private limit(value: string, maxLength: number): string {
    if (!value) {
      return value;
    }

    return value.length > maxLength ? `${value.slice(0, maxLength)}...<truncated>` : value;
  }
}
